import {
  MOBILE_MEMBERSHIP_GUIDANCE_INAPP_TO_WEB_AVAILABLE,
  MOBILE_MEMBERSHIP_GUIDANCE_INAPP_TO_WEB_PENDING,
  MOBILE_MEMBERSHIP_WEB_CONVERSION_PENDING_STATE,
  MOBILE_MEMBERSHIP_WEB_SUBSCRIBED_STATE,
  PC_HERO_BADGE_STANDARD_TYPE,
  type MobileMembershipPolicy,
} from './mobile-membership-utils.ts';
import { ROUTES } from '../../routes/paths.ts';
import {
  createPaymentResultPath,
  type MembershipPaymentResult,
} from '../../utils/membership-payment-result.ts';

export const MOBILE_MEMBERSHIP_EARLYBIRD_MOCK_SCENARIO = 'earlybird';
export const MOBILE_MEMBERSHIP_APP_CONVERSION_MOCK_SCENARIO = 'app-conversion';
export const MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_SCENARIO = 'app-conversion-pending';
export const MOBILE_MEMBERSHIP_WEB_SUBSCRIBER_MOCK_SCENARIO = 'web-subscriber';
export const MOBILE_MEMBERSHIP_MOCK_SCENARIO = MOBILE_MEMBERSHIP_EARLYBIRD_MOCK_SCENARIO;
export const MOBILE_MEMBERSHIP_MOCK_QUERY_KEY = 'mock';
export const MOBILE_MEMBERSHIP_MOCK_STORAGE_KEY = 'mobileMembershipMockScenario';
export const MOBILE_MEMBERSHIP_MOCK_TOKEN = 'mock.mobile.membership';
export const MOBILE_MEMBERSHIP_MOCK_PAYMENT_RESULT_ID = 'mock-mobile-membership-payment-result';
export const MOBILE_MEMBERSHIP_APP_CONVERSION_MOCK_PAYMENT_RESULT_ID =
  'mock-mobile-membership-app-conversion-result';
export const MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_PAYMENT_RESULT_ID =
  'mock-mobile-membership-app-conversion-pending-result';

const PAYMENT_METHOD_STORAGE_KEY = 'mobileMembershipPaymentMethod';
const PAYMENT_AMOUNT_STORAGE_KEY = 'mobileMembershipPaymentAmountLabel';
const MOBILE_MEMBERSHIP_MOCK_ENABLED = false;
const MOBILE_MEMBERSHIP_MOCK_LIVE_HOSTNAME = 'app.sellerbox.io';
const MOBILE_MEMBERSHIP_MOCK_ALLOWED_HOSTNAMES = new Set(['localhost', 'app-dev.sellerbox.io']);
const MOBILE_MEMBERSHIP_MOCK_SCENARIOS = new Set<string>([
  MOBILE_MEMBERSHIP_EARLYBIRD_MOCK_SCENARIO,
  MOBILE_MEMBERSHIP_APP_CONVERSION_MOCK_SCENARIO,
  MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_SCENARIO,
  MOBILE_MEMBERSHIP_WEB_SUBSCRIBER_MOCK_SCENARIO,
]);

export type MobileMembershipMockScenario =
  | typeof MOBILE_MEMBERSHIP_EARLYBIRD_MOCK_SCENARIO
  | typeof MOBILE_MEMBERSHIP_APP_CONVERSION_MOCK_SCENARIO
  | typeof MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_SCENARIO
  | typeof MOBILE_MEMBERSHIP_WEB_SUBSCRIBER_MOCK_SCENARIO;

type MobileMembershipMockPaymentInfo = {
  PaymentAmount: number;
  CardName: string;
  CardNo: string;
  Period: string;
};

export const MOCK_MOBILE_MEMBERSHIP_POLICY: MobileMembershipPolicy = {
  productName: '셀러박스 멤버십',
  subscriptionNo: 24,
  billingCycle: 'MONTHLY',
  hasBillingMethod: false,
  currentSubscription: null,
  pendingSubscription: {
    exists: false,
    eligibleDt: '',
    maxCount: 0,
    paymentResultId: 0,
    status: '',
  },
  guidance: {
    type: null,
    headline: '',
    ctaLabel: '',
  },
  isAnySubscribed: false,
  isPrePayment: false,
  regularAmount: 15000,
  paymentAmount: 15000,
  nextPaymentAmount: 100,
  currency: 'KRW',
  trialDays: 0,
  promotion: {
    enabled: true,
    badgeText: '🎉 얼리버드 이벤트 사전 가입',
    heroTitle: '지금 가입하면\n6월부터 3개월 월 100원',
    summaryText: '🎉6월부터 3개월간 100원으로 결제 돼요!',
    priceDescription: '5월 가입 시 15,000원\n6월부터 3개월간 월 100원 적용',
    periodText: '6~8월',
    discountMonths: 3,
    discountAmount: 100,
  },
  paymentSummary: {
    currentPaymentLabel: '15,000원',
    regularAmountLabel: '15,000원',
    billingCycleLabel: '매월 자동 결제',
    noticeText: '',
    ctaLabel: '15,000원 결제하기',
  },
  pcHeroBadge: {
    enabled: false,
    label: '',
    type: PC_HERO_BADGE_STANDARD_TYPE,
  },
};

export const MOCK_MOBILE_MEMBERSHIP_PAYMENT_INFO = {
  PaymentAmount: 15000,
  CardName: '신한카드 1234',
  CardNo: '1234',
  Period: '2026.06.01 ~ 2026.06.30',
};

export const MOCK_APP_CONVERSION_MOBILE_MEMBERSHIP_POLICY: MobileMembershipPolicy = {
  ...MOCK_MOBILE_MEMBERSHIP_POLICY,
  currentSubscription: {
    subscriptionNo: 23,
    channel: 'APP',
    expireDt: '2026.06.01 23:59:59',
    isExpiringSoon: true,
  },
  guidance: {
    type: MOBILE_MEMBERSHIP_GUIDANCE_INAPP_TO_WEB_AVAILABLE,
    headline: '현재 이용 중인 멤버십 기간 종료 후 할인 금액이 결제됩니다.',
    ctaLabel: '100원 혜택 받기',
  },
  isAnySubscribed: true,
  isPrePayment: true,
  paymentAmount: 0,
  nextPaymentAmount: 100,
  promotion: {
    ...MOCK_MOBILE_MEMBERSHIP_POLICY.promotion,
    summaryText: '🎉 얼리버드 이벤트 · 6~8월 3개월 월 100원',
  },
  paymentSummary: {
    ...MOCK_MOBILE_MEMBERSHIP_POLICY.paymentSummary,
    currentPaymentLabel: '100원',
    noticeText: '',
    ctaLabel: '100원 혜택 받기',
  },
};

export const MOCK_APP_CONVERSION_PENDING_MOBILE_MEMBERSHIP_POLICY: MobileMembershipPolicy = {
  ...MOCK_APP_CONVERSION_MOBILE_MEMBERSHIP_POLICY,
  hasBillingMethod: true,
  membershipState: MOBILE_MEMBERSHIP_WEB_CONVERSION_PENDING_STATE,
  pendingPaymentResultId: MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_PAYMENT_RESULT_ID,
  pendingSubscription: {
    exists: true,
    eligibleDt: '2026.07.01 00:00:00',
    maxCount: 3,
    paymentResultId: 1234,
    status: 'PENDING',
  },
  guidance: {
    type: MOBILE_MEMBERSHIP_GUIDANCE_INAPP_TO_WEB_PENDING,
    headline: '웹 멤버십 예약이 완료됐어요',
    ctaLabel: '예약 확인하기',
  },
};

export const MOCK_APP_CONVERSION_MOBILE_PAYMENT_INFO = {
  PaymentAmount: 15000,
  CardName: '신한카드 1234',
  CardNo: '1234',
  Period: '2026.06.01 ~ 2026.06.01',
};

export const MOCK_APP_CONVERSION_PENDING_MOBILE_PAYMENT_INFO = {
  PaymentAmount: 100,
  CardName: '신한카드 1234',
  CardNo: '1234',
  Period: '2026.06.01 ~ 2026.06.30',
};

export const MOCK_WEB_SUBSCRIBER_MOBILE_PAYMENT_INFO = {
  PaymentAmount: 100,
  CardName: '신한카드 1234',
  CardNo: '1234',
  Period: '2026.06.01 ~ 2026.06.30',
};

export const MOCK_WEB_SUBSCRIBER_MOBILE_MEMBERSHIP_POLICY: MobileMembershipPolicy = {
  ...MOCK_MOBILE_MEMBERSHIP_POLICY,
  membershipState: MOBILE_MEMBERSHIP_WEB_SUBSCRIBED_STATE,
  isAnySubscribed: true,
  isPrePayment: false,
  paymentAmount: 15000,
  nextPaymentAmount: 15000,
  promotion: {
    ...MOCK_MOBILE_MEMBERSHIP_POLICY.promotion,
    enabled: false,
  },
  paymentSummary: {
    ...MOCK_MOBILE_MEMBERSHIP_POLICY.paymentSummary,
    currentPaymentLabel: '15,000원',
    ctaLabel: '구독중',
  },
};

export const getIsMobileMembershipMockAllowedHostname = (hostname: string) => {
  if (!MOBILE_MEMBERSHIP_MOCK_ENABLED) return false;

  const normalizedHostname = hostname.toLowerCase();
  if (normalizedHostname === MOBILE_MEMBERSHIP_MOCK_LIVE_HOSTNAME) return false;

  return MOBILE_MEMBERSHIP_MOCK_ALLOWED_HOSTNAMES.has(normalizedHostname);
};

export const getIsMobileMembershipMockAllowed = () => {
  if (!MOBILE_MEMBERSHIP_MOCK_ENABLED) return false;
  if (typeof window === 'undefined') return true;

  return getIsMobileMembershipMockAllowedHostname(window.location.hostname);
};

const readMockScenario = (value: string | null): MobileMembershipMockScenario | null => {
  if (!value || !MOBILE_MEMBERSHIP_MOCK_SCENARIOS.has(value)) return null;

  return value as MobileMembershipMockScenario;
};

export const getMobileMembershipMockScenario = (searchParams?: URLSearchParams) => {
  if (!getIsMobileMembershipMockAllowed()) return null;

  const params =
    searchParams ??
    (typeof window === 'undefined'
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search));
  const redirectPath = params.get('redirect') ?? '';
  const redirectSearch = redirectPath.includes('?') ? redirectPath.split('?')[1] : '';
  const redirectParams = new URLSearchParams(redirectSearch);
  const storedScenario =
    typeof localStorage === 'undefined'
      ? null
      : localStorage.getItem(MOBILE_MEMBERSHIP_MOCK_STORAGE_KEY);

  return (
    readMockScenario(params.get(MOBILE_MEMBERSHIP_MOCK_QUERY_KEY)) ??
    readMockScenario(redirectParams.get(MOBILE_MEMBERSHIP_MOCK_QUERY_KEY)) ??
    readMockScenario(storedScenario)
  );
};

export const getIsMobileMembershipMockEnabled = () => {
  if (!getIsMobileMembershipMockAllowed()) return false;

  return readMockScenario(localStorage.getItem(MOBILE_MEMBERSHIP_MOCK_STORAGE_KEY)) !== null;
};

export const getShouldUseMobileMembershipMock = (searchParams?: URLSearchParams) => {
  return getMobileMembershipMockScenario(searchParams) !== null;
};

export const appendMobileMembershipMockQuery = (
  path: string,
  scenario: MobileMembershipMockScenario = getMobileMembershipMockScenario() ??
    MOBILE_MEMBERSHIP_EARLYBIRD_MOCK_SCENARIO,
) => {
  const [pathname, queryString = ''] = path.split('?');
  const params = new URLSearchParams(queryString);
  params.set(MOBILE_MEMBERSHIP_MOCK_QUERY_KEY, scenario);

  return `${pathname}?${params.toString()}`;
};

export const createMobileMembershipMockPaymentResultPath = () => {
  const mockScenario = getMobileMembershipMockScenario();
  const paymentResultId =
    mockScenario === MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_SCENARIO
      ? MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_PAYMENT_RESULT_ID
      : mockScenario === MOBILE_MEMBERSHIP_APP_CONVERSION_MOCK_SCENARIO
      ? MOBILE_MEMBERSHIP_APP_CONVERSION_MOCK_PAYMENT_RESULT_ID
      : MOBILE_MEMBERSHIP_MOCK_PAYMENT_RESULT_ID;

  return createPaymentResultPath(ROUTES.mobileMembershipComplete, paymentResultId);
};

export const startMobileMembershipMockScenario = (
  scenario: MobileMembershipMockScenario = getMobileMembershipMockScenario() ??
    MOBILE_MEMBERSHIP_EARLYBIRD_MOCK_SCENARIO,
) => {
  localStorage.setItem(MOBILE_MEMBERSHIP_MOCK_STORAGE_KEY, scenario);
  localStorage.removeItem('cplatToken');
  localStorage.removeItem(PAYMENT_METHOD_STORAGE_KEY);
  localStorage.removeItem(PAYMENT_AMOUNT_STORAGE_KEY);
};

export const completeMobileMembershipMockAuthentication = (
  scenario: MobileMembershipMockScenario = getMobileMembershipMockScenario() ??
    MOBILE_MEMBERSHIP_EARLYBIRD_MOCK_SCENARIO,
) => {
  localStorage.setItem(MOBILE_MEMBERSHIP_MOCK_STORAGE_KEY, scenario);
  localStorage.setItem('cplatToken', MOBILE_MEMBERSHIP_MOCK_TOKEN);
};

export const storeMobileMembershipMockPayment = (paymentMethod: string) => {
  const policy = getMobileMembershipMockPolicy();

  localStorage.setItem(PAYMENT_METHOD_STORAGE_KEY, paymentMethod);
  localStorage.setItem(PAYMENT_AMOUNT_STORAGE_KEY, policy.paymentSummary.currentPaymentLabel);
};

export const getMobileMembershipMockPolicy = () => {
  if (
    getMobileMembershipMockScenario() === MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_SCENARIO
  ) {
    return MOCK_APP_CONVERSION_PENDING_MOBILE_MEMBERSHIP_POLICY;
  }
  if (getMobileMembershipMockScenario() === MOBILE_MEMBERSHIP_APP_CONVERSION_MOCK_SCENARIO) {
    return MOCK_APP_CONVERSION_MOBILE_MEMBERSHIP_POLICY;
  }
  if (getMobileMembershipMockScenario() === MOBILE_MEMBERSHIP_WEB_SUBSCRIBER_MOCK_SCENARIO) {
    return MOCK_WEB_SUBSCRIBER_MOBILE_MEMBERSHIP_POLICY;
  }

  return MOCK_MOBILE_MEMBERSHIP_POLICY;
};

export const getMobileMembershipMockPaymentInfo = () => {
  if (
    getMobileMembershipMockScenario() === MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_SCENARIO
  ) {
    return MOCK_APP_CONVERSION_PENDING_MOBILE_PAYMENT_INFO;
  }
  if (getMobileMembershipMockScenario() === MOBILE_MEMBERSHIP_APP_CONVERSION_MOCK_SCENARIO) {
    return MOCK_APP_CONVERSION_MOBILE_PAYMENT_INFO;
  }
  if (getMobileMembershipMockScenario() === MOBILE_MEMBERSHIP_WEB_SUBSCRIBER_MOCK_SCENARIO) {
    return MOCK_WEB_SUBSCRIBER_MOBILE_PAYMENT_INFO;
  }

  return MOCK_MOBILE_MEMBERSHIP_PAYMENT_INFO;
};

export const getMobileMembershipMockPaymentResult = (): MembershipPaymentResult => {
  const mockScenario = getMobileMembershipMockScenario();
  const policy = getMobileMembershipMockPolicy();
  const paymentInfo = getMobileMembershipMockPaymentInfo();
  const basePaymentResult = getBaseMockPaymentResult(mockScenario, paymentInfo, policy);
  const storedPaymentAmount = localStorage.getItem(PAYMENT_AMOUNT_STORAGE_KEY);
  const storedPaymentMethod = localStorage.getItem(PAYMENT_METHOD_STORAGE_KEY);

  return {
    ...basePaymentResult,
    paymentAmountLabel: storedPaymentAmount ?? basePaymentResult.paymentAmountLabel,
    paymentMethodLabel: storedPaymentMethod ?? basePaymentResult.paymentMethodLabel,
  };
};

const getBaseMockPaymentResult = (
  mockScenario: MobileMembershipMockScenario | null,
  paymentInfo: MobileMembershipMockPaymentInfo,
  policy: MobileMembershipPolicy,
) => {
  if (mockScenario === MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_SCENARIO) {
    return createAppConversionPendingMockPaymentResult(paymentInfo, policy);
  }
  if (mockScenario === MOBILE_MEMBERSHIP_APP_CONVERSION_MOCK_SCENARIO) {
    return createAppConversionMockPaymentResult(paymentInfo, policy);
  }

  return createEarlybirdMockPaymentResult(paymentInfo, policy);
};

const createAppConversionPendingMockPaymentResult = (
  paymentInfo: MobileMembershipMockPaymentInfo,
  policy: MobileMembershipPolicy,
): MembershipPaymentResult => {
  return {
    productName: policy.productName,
    subscriptionNo: policy.subscriptionNo,
    paymentAmount: policy.nextPaymentAmount,
    paymentAmountLabel: policy.paymentSummary.currentPaymentLabel,
    paymentMethodLabel: paymentInfo.CardName,
    periodLabel: '2026.07.01 ~ 2026.07.30',
    nextPaymentDateLabel: '2026.07.01',
    statusLabel: '결제 예정',
    promotionNotice: {
      enabled: false,
      title: '',
      description: '',
    },
  };
};

const createEarlybirdMockPaymentResult = (
  paymentInfo: MobileMembershipMockPaymentInfo,
  policy: MobileMembershipPolicy,
): MembershipPaymentResult => {
  return {
    productName: policy.productName,
    subscriptionNo: policy.subscriptionNo,
    paymentAmount: paymentInfo.PaymentAmount,
    paymentAmountLabel: policy.paymentSummary.currentPaymentLabel,
    paymentMethodLabel: paymentInfo.CardName,
    periodLabel: paymentInfo.Period,
    nextPaymentDateLabel: getNextDateLabel(paymentInfo.Period),
    statusLabel: '이용중',
    promotionNotice: {
      enabled: true,
      title: '6월 한정 이벤트 적용 중',
      description:
        '8월까지 매월 100원 자동 결제되며, 9월부터 정상가 15,000원으로 결제돼요. 언제든 해지 가능합니다.',
    },
  };
};

const createAppConversionMockPaymentResult = (
  paymentInfo: MobileMembershipMockPaymentInfo,
  policy: MobileMembershipPolicy,
): MembershipPaymentResult => {
  const scheduledPaymentDate = getNextDateFromPeriod(paymentInfo.Period);
  const scheduledPeriodLabel = scheduledPaymentDate
    ? createMonthlyPeriodLabel(scheduledPaymentDate)
    : '앱에서 확인 가능';

  return {
    productName: policy.productName,
    subscriptionNo: policy.subscriptionNo,
    paymentAmount: policy.nextPaymentAmount,
    paymentAmountLabel: policy.paymentSummary.currentPaymentLabel,
    paymentMethodLabel: paymentInfo.CardName,
    periodLabel: scheduledPeriodLabel,
    nextPaymentDateLabel: scheduledPaymentDate
      ? formatDateLabel(scheduledPaymentDate)
      : '앱에서 확인 가능',
    statusLabel: '결제 예정',
    promotionNotice: {
      enabled: false,
      title: '',
      description: '',
    },
  };
};

const getNextDateLabel = (period: string) => {
  const nextDate = getNextDateFromPeriod(period);

  return nextDate ? formatDateLabel(nextDate) : '앱에서 확인 가능';
};

const getNextDateFromPeriod = (period: string) => {
  const endDateLabel = period.split('~')[1]?.trim();
  const endDate = endDateLabel ? parseDateLabel(endDateLabel) : null;
  if (!endDate) return null;

  return addDays(endDate, 1);
};

const createMonthlyPeriodLabel = (startDate: Date) => {
  const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

  return `${formatDateLabel(startDate)} ~ ${formatDateLabel(endDate)}`;
};

const parseDateLabel = (dateLabel: string) => {
  const [year, month, day] = dateLabel.split('.').map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;

  return date;
};

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);

  return nextDate;
};

const formatDateLabel = (date: Date) => {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('.');
};

export const getMobileMembershipMockUser = () => {
  const mockScenario = getMobileMembershipMockScenario();
  const isAppMembership =
    mockScenario === MOBILE_MEMBERSHIP_APP_CONVERSION_MOCK_SCENARIO ||
    mockScenario === MOBILE_MEMBERSHIP_APP_CONVERSION_PENDING_MOCK_SCENARIO;
  const isWebSubscriber = mockScenario === MOBILE_MEMBERSHIP_WEB_SUBSCRIBER_MOCK_SCENARIO;

  return {
    CplatToken: MOBILE_MEMBERSHIP_MOCK_TOKEN,
    VerificationToken: '',
    Email: 'mock@sellerbox.io',
    UserNo: 'mock-mobile-membership-user',
    Password: '',
    Name: '목 가입자',
    PhoneNumber: '01012345678',
    UseYn: 'Y',
    LastLoginDate: '2026-05-15',
    SnsType: '',
    SnsToken: '',
    PushAlertToken: '',
    InvitationCode: '',
    BizNo: '',
    BizType: '',
    BizNm: '',
    StoreName: '셀러박스 목 스토어',
    StoreCategory1: '',
    StoreCategory2: '',
    StoreSinceWhenSales: '',
    StoreThumbnailImageUrl: '',
    AdAgreeYn: 'N',
    DailyReportYn: 'Y',
    DailyAmountAlertYn: 'Y',
    DailyAlertAmount: 0,
    UndeliveryAlertYn: 'Y',
    UndeliveryAlertTime: '',
    OrderRealtimeAlertYn: 'Y',
    RefundRealtimeAlertYn: 'Y',
    ReviewRealtimeAlertYn: 'Y',
    ReviewLowScoreAlertYn: 'Y',
    ReviewLowScoreCanBeSet: 0,
    KcbCreditRetrieveAgreeYn: 'Y',
    GlobalIncludeShippingFeeYn: 'Y',
    RegisterDate: '2026-05-15',
    ServiceInfo: {
      AiSellerInfo: {
        IsSubcribed: false,
      },
      SellkeyInfo: {
        IsSubcribed: false,
        FirstLoginDate: '2026-05-15',
        IsTrialPeriod: false,
      },
      AppSellerInfo: {
        IsAnySubscribed: isAppMembership || isWebSubscriber,
        SubscriptionNo: isAppMembership ? 23 : 24,
      },
    },
    SellkeyMonitoringAlertYn: 'Y',
    PreferShippingCompanyList: [],
    EmailAddreess: 'mock@sellerbox.io',
  };
};
